import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { AuthShell, GoogleButton } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(data.role ? "/dashboard" : "/role-select");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const demo = (em) => { setEmail(em); setPassword("Passw0rd!"); };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to manage your driving lessons.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="you@example.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 rounded-full bg-arctic-primary hover:bg-arctic-primary/90">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">OR</span><div className="flex-1 h-px bg-border" />
      </div>
      <GoogleButton />

      <p className="text-sm text-muted-foreground mt-6 text-center">
        No account? <Link to="/register" className="text-arctic-primary font-semibold hover:underline" data-testid="to-register">Sign up</Link>
      </p>

      <div className="mt-6 rounded-xl border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        <div className="font-semibold text-arctic-secondary mb-1">Demo accounts (password: Passw0rd!)</div>
        <button type="button" onClick={() => demo("student@arcticdrive.com")} className="block hover:text-arctic-primary" data-testid="demo-student">student@arcticdrive.com</button>
        <button type="button" onClick={() => demo("instructor@arcticdrive.com")} className="block hover:text-arctic-primary" data-testid="demo-instructor">instructor@arcticdrive.com</button>
      </div>
    </AuthShell>
  );
}
